import type { Component } from "vue";
import {
  BarChart3,
  Bell,
  BookOpen,
  Building2,
  CreditCard,
  FileSearch,
  FileText,
  HandCoins,
  LayoutDashboard,
  ListTree,
  MessageSquare,
  Scale,
  ScrollText,
  Settings,
  Upload,
  Users,
  UsersRound,
  Wallet,
  Webhook,
} from "lucide-vue-next";
import {
  PLATFORM_FEATURE_DEFINITIONS,
  PLATFORM_MENU_DEFINITIONS,
  PlatformMenuKey,
} from "./platformFeatures.generated";

export type MenuKey = PlatformMenuKey;

export interface MenuDefinition {
  key: MenuKey;
  path: string;
  label: string;
  labelKey: string;
  icon: Component;
  subtitle: string;
  subtitleKey?: string;
}

const ICONS: Record<string, Component> = {
  BarChart3,
  Bell,
  BookOpen,
  Building2,
  CreditCard,
  FileSearch,
  FileText,
  HandCoins,
  LayoutDashboard,
  ListTree,
  MessageSquare,
  Scale,
  ScrollText,
  Settings,
  Upload,
  Users,
  UsersRound,
  Wallet,
  Webhook,
};

const FIRST_LAUNCH_MENU_DEFINITIONS = PLATFORM_MENU_DEFINITIONS.filter(
  (item) => !('firstLaunch' in item) || item.firstLaunch !== false,
);

export const MENU_DEFINITIONS: MenuDefinition[] = FIRST_LAUNCH_MENU_DEFINITIONS.map((item) => ({
  key: item.key,
  path: item.route,
  label: item.label,
  labelKey: item.labelKey,
  icon: ICONS[item.icon] || LayoutDashboard,
  subtitle: item.subtitle,
  subtitleKey: 'subtitleKey' in item ? item.subtitleKey : undefined,
}));

const PHASE1_MENU_KEYS = new Set(MENU_DEFINITIONS.map((item) => item.key));

export function isPhase1MenuKey(key: string): boolean {
  return PHASE1_MENU_KEYS.has(key as MenuKey);
}

export function menuKeyFromPath(path: string): MenuKey | null {
  const normalized = path.replace(/\/$/, "") || "/";
  if (normalized.startsWith("/team")) return "team";
  if (normalized.startsWith("/employees")) return "employee-management";
  // Prefer longest route prefix so /documents/ledger maps to ledger, not documents.
  const prefixMatch = MENU_DEFINITIONS
    .filter((item) => item.path !== "/")
    .sort((left, right) => right.path.length - left.path.length)
    .find((item) => normalized === item.path || normalized.startsWith(`${item.path}/`));
  if (prefixMatch) return prefixMatch.key;
  const exactMatch = MENU_DEFINITIONS.find((item) => item.path === normalized);
  return exactMatch?.key ?? null;
}

export function pairGrantedMenus(allowedMenus: string[]): string[] {
  const next = [...allowedMenus];
  if (next.includes("accounting-books") && !next.includes("chart-of-accounts")) {
    next.splice(next.indexOf("accounting-books") + 1, 0, "chart-of-accounts");
  }
  if (next.includes("document-intake") && !next.includes("document-summary")) {
    next.splice(next.indexOf("document-intake") + 1, 0, "document-summary");
  }
  return next;
}

export function getVisibleMenus(allowedMenus: string[] | undefined) {
  if (!allowedMenus?.length) return MENU_DEFINITIONS;
  const allowed = new Set(pairGrantedMenus(allowedMenus));
  return MENU_DEFINITIONS.filter((m) => allowed.has(m.key));
}

export function defaultPlanFeaturesFallback() {
  return {
    menus: MENU_DEFINITIONS.map((m) => m.key),
    features: Object.fromEntries(
      PLATFORM_FEATURE_DEFINITIONS.map((feature) => [feature.key, true]),
    ),
  };
}
